import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/Auth';
import { usePoints } from '../../context/PointsContext';
import Navigation from '../Home/Navigation';
import PointsBalanceCard from '../Points/PointsBalanceCard';
import UsageIndicator from '../Points/UsageIndicator';
import VeoCreditIndicator from './VeoCreditIndicator';
import { veoCreditsService } from '../../services/veoCreditsService';
import styles from './UsagePage.module.css';
import { getApiUrl } from '../../config/api';

// FIXED: Updated interface to match backend response structure exactly
interface UsageSummaryResponse {
  planType: 'traditional' | 'points' | 'unlimited';
  plan: string;
  traditionalUsage: {
    plan: string;
    billingPeriod: {
      start: string | null;
      end: string | null;
    };
    usage: {
      captions: { used: number; limit: number; remaining: number | 'unlimited' };
      thumbnails: { used: number; limit: number; remaining: number | 'unlimited' };
      voiceSynthesis: { used: number; limit: number; remaining: number | 'unlimited' };
      musicGeneration: { used: number; limit: number; remaining: number | 'unlimited' };
      titleDescriptions: { used: number; limit: number; remaining: number | 'unlimited' };
      imageAnalyses: { used: number; limit: number; remaining: number | 'unlimited' };
      imagesGenerated: { used: number; limit: number; remaining: number | 'unlimited' };
      videosGenerated: { used: number; limit: number; remaining: number | 'unlimited' };
      prompts: { used: number; limit: number | 'unlimited'; remaining: number | 'unlimited' };
      scripts: { used: number; limit: number; remaining: number | 'unlimited' };
    };
    creditBalance: number;
    individualAppSubscriptions: {
      caption: boolean;
      thumbnail: boolean;
      audio: boolean;
      title: boolean;
      prompt: boolean;
      imageAnalysis: boolean;
    };
  };
  pointsBalance?: {
    monthlyPoints: number;
    pointsUsed: number;
    pointsRemaining: number;
    lastReset: Date;
    isUnlimited?: boolean;
    dailyUsed?: number;
    dailyLimit?: number;
  };
}

const UsagePage: React.FC = () => {
  const { user } = useAuth();
  const { isEnabled: pointsEnabled, planType, refreshBalance } = usePoints();
  const [usageData, setUsageData] = useState<UsageSummaryResponse | null>(null);
  const [hasVeoCredits, setHasVeoCredits] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    fetchUsageData();
    checkVeoCredits();
    
    // Set up automatic refresh every 30 seconds for real-time updates
    const interval = setInterval(() => {
      if (!loading) {
        refreshBalance?.();
        fetchUsageData();
        checkVeoCredits();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [loading, refreshBalance]);

  // Handle VEO purchase success/cancellation from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const veoPurchase = urlParams.get('veo-purchase');
    
    if (veoPurchase === 'success') {
      // Refresh VEO credit data after successful purchase
      setTimeout(() => {
        checkVeoCredits();
        fetchUsageData();
      }, 1000); // Small delay to allow webhook processing
      
      // Remove the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (veoPurchase === 'cancelled') {
      // Handle cancellation if needed
      console.log('VEO credit purchase was cancelled');
      
      // Remove the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkVeoCredits = async () => {
    try {
      const hasCredits = await veoCreditsService.hasVeoCredits();
      setHasVeoCredits(hasCredits);
    } catch (error) {
      console.error('Error checking VEO credits:', error);
      // Don't show VEO section if there's an error checking
      setHasVeoCredits(false);
    }
  };

  const handleVeoRefresh = () => {
    checkVeoCredits();
    fetchUsageData();
  };

  const fetchUsageData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(getApiUrl('/usage/summary'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        if (response.status === 404) {
          throw new Error('Please log out and log back in to access the new subscription features');
        }
        if (response.status === 500) {
          throw new Error('Server error. Please try again later.');
        }
        throw new Error(`Failed to fetch usage data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Usage data received:', data);
      
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid usage data format received from server');
      }

      setUsageData(data);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Please log in to manage your billing');
      }

      const response = await fetch(getApiUrl('/billing/create-portal-session'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/usage`
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error('Failed to access billing portal. Please try again.');
      }

      const { portalUrl } = await response.json();
      
      // Redirect to Stripe's customer portal
      window.location.href = portalUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to access billing portal');
      setBillingLoading(false);
    }
  };

  const handleCreditPurchase = async () => {
    if (!user?.plan || user.plan === 'creator' || user.plan === 'free') {
      setError('Credit purchasing is only available for paid plan subscribers');
      return;
    }

    if (user.plan !== 'creator_studio' && user.plan !== 'creator_unlimited') {
      setError('Credit purchasing is only available for Creator Studio and Creator Unlimited subscribers');
      return;
    }

    setBillingLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/billing/create-credit-session'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creditAmount: 25, // Default to 25 credits
          successUrl: `${window.location.origin}/usage?purchased=credits&`,
          cancelUrl: `${window.location.origin}/usage`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create credit purchase session');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purchase credits');
      setBillingLoading(false);
    }
  };

  const getPlanDisplayName = (plan: string): string => {
    switch (plan?.toLowerCase()) {
      case 'creator': return '🆓 Creator (Free)';
      case 'creator_lite': return '⚡ Creator Lite';
      case 'creator_pro': return '🚀 Creator Pro';
      case 'creator_studio': return '🎬 Creator Studio';
      case 'creator_unlimited': return '🌟 Creator Unlimited';
      default: return plan?.charAt(0).toUpperCase() + plan?.slice(1).replace(/_/g, ' ') || 'Unknown Plan';
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <Navigation />
        <div className={styles.container}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading usage data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <Navigation />
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <h2>Error Loading Usage Data</h2>
            <p>{error}</p>
            <button 
              className={styles.retryButton}
              onClick={fetchUsageData}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!usageData?.traditionalUsage) {
    return (
      <div className={styles.pageContainer}>
        <Navigation />
        <div className={styles.container}>
          <div className={styles.errorContainer}>
            <h2>No Usage Data Available</h2>
            <p>Unable to load your usage information. This could be because:</p>
            <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
              <li>Your account needs to be updated in our system</li>
              <li>You don't have an active subscription</li>
              <li>There's a temporary server issue</li>
            </ul>
            <div style={{ marginTop: '2rem' }}>
              <button 
                className={styles.retryButton}
                onClick={fetchUsageData}
                style={{ marginRight: '1rem' }}
              >
                Retry Loading
              </button>
              <button 
                className={styles.retryButton}
                onClick={() => window.location.href = '/pricing'}
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { traditionalUsage, pointsBalance } = usageData;

  return (
    <div className={styles.pageContainer}>
      <Navigation />
      <div className={styles.container}>
        {/* Error Banner */}
        {error && (
          <div className={styles.errorBanner}>
            <p>⚠️ {error}</p>
            <button onClick={() => setError('')} className={styles.closeError}>×</button>
          </div>
        )}

        <div className={styles.header}>
          <h1 className={styles.title}>Usage Dashboard</h1>
          <div className={styles.headerInfo}>
            <div className={styles.planInfo}>
              <span className={styles.currentPlan}>
                {getPlanDisplayName(traditionalUsage.plan)}
              </span>
              {traditionalUsage.plan === 'ministry_pro' && (
                <div className={styles.creditBalance}>
                  <span className={styles.creditIcon}>💎</span>
                  <span className={styles.creditText}>
                    {traditionalUsage.creditBalance || 0} Credits
                  </span>
                </div>
              )}
            </div>
            {traditionalUsage.billingPeriod?.start && (
              <div className={styles.billingPeriod}>
                <span className={styles.periodText}>
                  Billing Period: {formatDate(traditionalUsage.billingPeriod.start)} - {formatDate(traditionalUsage.billingPeriod.end)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Points Balance Card - only show if points are enabled */}
        {pointsEnabled && <PointsBalanceCard />}

        {/* VEO Credit Indicator - only show if user has VEO credits */}
        {hasVeoCredits && (
          <div className={styles.veoSection}>
            <VeoCreditIndicator onRefresh={handleVeoRefresh} />
          </div>
        )}

        <div className={styles.usageGrid}>
          {/* ALL FEATURES - Show different view for points vs traditional users */}
          <UsageIndicator
            label="AI Video Captions"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.captions?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.captions?.limit || 0}
            icon="📝"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.caption || false}
            featureType="CAPTION"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Thumbnails"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.thumbnails?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.thumbnails?.limit || 0}
            icon="🖼️"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.thumbnail || false}
            featureType="THUMBNAIL"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Voice Generation"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.voiceSynthesis?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.voiceSynthesis?.limit || 0}
            icon="🎤"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.audio || false}
            featureType="VOICE_SYNTHESIS"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Music Generation"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.musicGeneration?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.musicGeneration?.limit || 0}
            icon="🎵"
            featureType="MUSIC_GENERATION"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="Smart Titles & Descriptions"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.titleDescriptions?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.titleDescriptions?.limit || 0}
            icon="✍️"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.title || false}
            featureType="TITLE_DESCRIPTION"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Image Analysis"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.imageAnalyses?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.imageAnalyses?.limit || 0}
            icon="🔍"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.imageAnalysis || false}
            featureType="IMAGE_ANALYSIS"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Image Creation"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.imagesGenerated?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.imagesGenerated?.limit || 0}
            icon="🎨"
            featureType="IMAGE_GENERATION"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Video Creation"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.videosGenerated?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.videosGenerated?.limit || 0}
            icon="🎬"
            featureType="VIDEO_GENERATION"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="Smart Prompt Generator"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.prompts?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.prompts?.limit || 'unlimited'}
            icon="💡"
            hasAppSubscription={traditionalUsage.individualAppSubscriptions?.prompt || false}
            featureType="PROMPT"
            pointsOnly={pointsEnabled}
          />

          <UsageIndicator
            label="AI Script Writing"
            used={pointsEnabled ? undefined : traditionalUsage.usage?.scripts?.used || 0}
            limit={pointsEnabled ? undefined : traditionalUsage.usage?.scripts?.limit || 0}
            icon="📝"
            featureType="SCRIPT"
            pointsOnly={pointsEnabled}
          />
        </div>

        <div className={styles.footer}>
          <div className={styles.upgradePrompt}>
            <h3>Need more resources?</h3>
            <p>Upgrade your plan to get higher limits and unlock premium features.</p>
            <button 
              className={styles.upgradeButton}
              onClick={() => window.location.href = '/pricing'}
            >
              <span className={styles.upgradeIcon}>⚡</span>
              Upgrade Plan
            </button>
          </div>

          {/* Billing Management Section */}
          {user?.plan && user.plan !== 'creator' && user.plan !== 'free' && (
            <div className={styles.billingSection}>
              <h3>Billing & Subscription Management</h3>
              <p>Manage your subscription, view invoices, and update payment methods.</p>
              
              <div className={styles.billingActions}>
                <button 
                  className={styles.billingButton}
                  onClick={handleManageBilling}
                  disabled={billingLoading}
                >
                  <span className={styles.billingIcon}>🔧</span>
                  {billingLoading ? 'Opening Portal...' : 'Manage Billing'}
                </button>

                {(user.plan === 'creator_studio' || user.plan === 'creator_unlimited') && (
                  <button 
                    className={styles.creditButton}
                    onClick={handleCreditPurchase}
                    disabled={billingLoading}
                  >
                    <span className={styles.creditIcon}>💳</span>
                    {billingLoading ? 'Processing...' : 'Buy Credits ($25)'}
                  </button>
                )}
              </div>

              <div className={styles.billingInfo}>
                <div className={styles.infoCard}>
                  <span className={styles.infoIcon}>👤</span>
                  <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>Current Plan</span>
                    <span className={styles.infoValue}>{getPlanDisplayName(user.plan)}</span>
                  </div>
                </div>

                {traditionalUsage?.creditBalance !== undefined && traditionalUsage.creditBalance > 0 && (
                  <div className={styles.infoCard}>
                    <span className={styles.infoIcon}>💰</span>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Credit Balance</span>
                      <span className={styles.infoValue}>{traditionalUsage.creditBalance} credits</span>
                    </div>
                  </div>
                )}

                {traditionalUsage?.billingPeriod?.end && (
                  <div className={styles.infoCard}>
                    <span className={styles.infoIcon}>📅</span>
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Next Billing</span>
                      <span className={styles.infoValue}>{formatDate(traditionalUsage.billingPeriod.end)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.billingNote}>
                <p>
                  <strong>Secure Billing:</strong> All billing is handled securely by Stripe. 
                  You'll be redirected to Stripe's secure portal to manage your subscription.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsagePage;